import { useState, useEffect } from 'react';
import { X, Anchor, Phone, Check, Upload, Loader2 } from 'lucide-react';
import { useQuotes } from '../../Hooks/useQuotes';
import { toast } from 'react-hot-toast';

const Info = ({ label, value, link, highlight }) => (
    <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        {link ? (
            <a href={link} className="font-semibold text-teal-600 hover:underline">{value || '—'}</a>
        ) : (
            <p className={`font-semibold ${highlight ? 'text-teal-600' : 'text-gray-900'}`}>{value || '—'}</p>
        )}
    </div>
);

export default function RequestModal({ quoteId, onClose, forceOfferForm = false }) {
    const { useQuoteDetails, createOffer, isCreatingOffer } = useQuotes();
    const { data: quote, isLoading } = useQuoteDetails(quoteId);

    const [showOfferForm, setShowOfferForm] = useState(false);
    const [price, setPrice] = useState('');
    const [notes, setNotes] = useState('');
    const [estimatedDays, setEstimatedDays] = useState('');
    const [reportGenerationFees, setReportGenerationFees] = useState('');
    const [agreementType, setAgreementType] = useState('signature');
    const [fees, setFees] = useState([
        { name: '', value: '', type: '' }
    ]);

    useEffect(() => {
        if (forceOfferForm && !isLoading && quote) {
            setShowOfferForm(true);
        }
    }, [forceOfferForm, isLoading, quote]);

    if (!quoteId) return null;
    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-12 text-center">
                    <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
                    <p className="mt-4 text-gray-600">Loading details...</p>
                </div>
            </div>
        );
    }
    if (!quote) {
        return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-8 text-center">
                    <p className="text-red-600">Failed to load request</p>
                    <button onClick={onClose} className="mt-4 text-teal-600 underline">Close</button>
                </div>
            </div>
        );
    }

    const client = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || 'Client';
    const files = quote.files || [];

    const handleFeeChange = (index, field, value) => {
        const updatedFees = [...fees];
        updatedFees[index] = { ...updatedFees[index], [field]: value };
        setFees(updatedFees);
    };

    const handleSendOffer = () => {
        if (!price) return toast.error('Price is required');

        // Validate Fuel fields
        const fuel = fees[0];
        if (!fuel.name || !fuel.value || !fuel.type) {
            return toast.error('All Fuel fields (Name, Value, Type) are required');
        }

        const validFees = fees
            .filter(fee => fee.name && fee.value && fee.type)
            .map(fee => ({
                name: fee.name,
                value: parseFloat(fee.value),
                type: fee.type,
            }));

        const payload = {
            price: parseFloat(price),
            message: notes || null,
            estimated_days: estimatedDays ? parseInt(estimatedDays) : null,
            report_generation_fees: reportGenerationFees ? parseFloat(reportGenerationFees) : null,
            agreement_type: agreementType,
            terms_and_conditions: '50% deposit to schedule.',
            ...(validFees.length > 0 && { fees: validFees }),
        };

        createOffer(
            {
                quoteId: quote.id,
                data: payload,
            },
            { onSuccess: () => { onClose(); } }
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[70vh]" onClick={e => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                                <Anchor className="w-9 h-9" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold">{client}</h2>
                                <p className="text-teal-100">{quote.service.name}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-7 overflow-y-auto h-[50vh]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Info label="Client" value={client} />
                        <Info label="Email" value={quote.email} link={`mailto:${quote.email}`} />
                        <Info label="Phone" value={quote.phone} link={`tel:${quote.phone}`} />
                        <Info label="Boat" value={`${quote.boat_make} ${quote.boat_year}`} />
                        <Info label="Type" value={quote.boat_type} />
                        <Info label="Length" value={`${quote.boat_length}ft`} />
                        <Info label="Location" value={quote.boat_location} />
                        <Info label="Survey Type" value={quote.service.name} highlight />
                    </div>

                    {quote.message && (
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Message</p>
                            <div className="bg-gray-50 p-4 rounded-lg text-sm">{quote.message}</div>
                        </div>
                    )}

                    {files.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Attached Files ({files.length})</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {files.map(file => (
                                    <a key={file.id} href={file.file_url} target="_blank" rel="noopener noreferrer"
                                        className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 border text-center">
                                        <Upload className="w-8 h-8 text-teal-600 mb-2" />
                                        <span className="text-xs line-clamp-2">{file.file_name.split('/').pop()}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {!showOfferForm ? (
                        <div className="flex justify-end gap-3 pt-6 border-t">
                            <a href={`tel:${quote.phone}`} className="px-6 py-3 bg-teal-600 text-white rounded-lg font-bold flex items-center gap-2">
                                <Phone className="w-4 h-4" /> Call Client
                            </a>
                            <button onClick={() => setShowOfferForm(true)} className="px-6 py-3 bg-teal-600 text-white rounded-lg font-bold">
                                Give Offer
                            </button>
                        </div>
                    ) : (
                        <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
                            <h3 className="text-lg font-bold mb-4">Send Your Quote</h3>

                            <div>
                                <label className="block text-xs font-semibold text-teal-700 mb-1.5 uppercase tracking-wider">Price (USD) *</label>
                                <input type="number" step="0.01" placeholder="Price (USD)" value={price} onChange={e => setPrice(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>

                            <div className="mt-4">
                                <label className="block text-xs font-semibold text-teal-700 mb-1.5 uppercase tracking-wider">Estimated days *</label>
                                <input type="number" placeholder="Estimated days" value={estimatedDays} onChange={e => setEstimatedDays(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>

                            <div className="mt-4">
                                <label className="block text-xs font-semibold text-teal-700 mb-1.5 uppercase tracking-wider">Report Generation Fees ($)</label>
                                <input type="number" step="0.01" placeholder="Report Generation Fees" value={reportGenerationFees} onChange={e => setReportGenerationFees(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>

                            <div className="mt-4">
                                <label className="block text-xs font-semibold text-teal-700 mb-1.5 uppercase tracking-wider">Agreement Type</label>
                                <select
                                    value={agreementType}
                                    onChange={e => setAgreementType(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                                >
                                    <option value="signature">Signature</option>
                                    <option value="text">Text</option>
                                </select>
                            </div>

                            <div className="mt-4">
                                <label className="block text-xs font-semibold text-teal-700 mb-1.5 uppercase tracking-wider">Notes (optional)</label>
                                <textarea rows={3} placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>

                            <div className="mt-4">
                                <label className="block text-xs font-semibold text-teal-700 mb-2 uppercase tracking-wider">Fuels *</label>
                                <div className="space-y-3">
                                    {fees.map((fee, index) => (
                                        <div key={index} className="flex gap-2 items-end bg-white p-3 rounded-lg border">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    placeholder="Fuel Name"
                                                    value={fee.name}
                                                    onChange={e => handleFeeChange(index, 'name', e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Value"
                                                    value={fee.value}
                                                    onChange={e => handleFeeChange(index, 'value', e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    placeholder="Type"
                                                    value={fee.type}
                                                    onChange={e => handleFeeChange(index, 'type', e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-5">
                                <button onClick={() => setShowOfferForm(false)} className="px-5 py-2.5 bg-gray-200 rounded-lg">Cancel</button>
                                <button onClick={handleSendOffer} disabled={isCreatingOffer} className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-70">
                                    {isCreatingOffer ? <>Sending...</> : <>Send Offer</>}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}